import type { SurveyScoresByCategory } from '@engagement-web-app/common';
import type Konva from 'konva';
import { useMemo, useRef, useState } from 'react';
import { Circle, Layer, Rect, Stage } from 'react-konva';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { Axes, X_TICK_INCREMENT, Y_TICK_INCREMENT } from './Axes';
import { DOT_FILL, DOT_OPACITY, STAGE_H, STAGE_W } from './constants';
import { useChartDimensions, useRange, useResponsiveStage } from './hooks';
import * as styles from './index.css';
import { Legend } from './Legend';
import { Quadrants } from './Quadrants';
import { Tooltip, type TooltipState } from './Tooltip';

export type DynamicAnalysisChartProps = {
    /** `null` while still loading. */
    scoresByCategory: SurveyScoresByCategory['categories'] | null;
    /** `null` while still loading. */
    previousScoresByCategory: SurveyScoresByCategory['categories'] | null;
    isError: boolean;
    onRetry: () => void;
};

export function DynamicAnalysisChart({
    scoresByCategory,
    previousScoresByCategory,
    isError,
    onRetry,
}: DynamicAnalysisChartProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="動的分析の取得に失敗しました。" />;

    if (!scoresByCategory || !previousScoresByCategory) return <DynamicAnalysisChartSkeleton />;

    return (
        <DynamicAnalysisChartContent
            scoresByCategory={scoresByCategory}
            previousScoresByCategory={previousScoresByCategory}
        />
    );
}

type DynamicAnalysisChartContentProps = {
    [K in keyof Pick<DynamicAnalysisChartProps, 'scoresByCategory' | 'previousScoresByCategory'>]: NonNullable<
        DynamicAnalysisChartProps[K]
    >;
};

// Split from the buffer above so its hooks are never called conditionally.
function DynamicAnalysisChartContent({ scoresByCategory, previousScoresByCategory }: DynamicAnalysisChartContentProps) {
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);
    const dotRefs = useRef<Map<string, Konva.Circle>>(new Map());

    // Only handle responsiveness for width, as height is fixed to align with the other charts.
    const { containerRef, width: stageWidth, stageRef } = useResponsiveStage(STAGE_W, STAGE_H);
    const chartDimensions = useChartDimensions(stageWidth, STAGE_H);
    const { left, top, width, height, xOrigin } = chartDimensions;

    const points = useMemo(() => {
        const prevById = new Map(previousScoresByCategory.map((c) => [c.id, c]));

        return scoresByCategory.flatMap((cat, i) => {
            const prev = cat.previousCategoryId ? prevById.get(cat.previousCategoryId) : undefined;

            if (!prev || cat.deviation == null || cat.di == null || prev.di == null) {
                return [];
            }

            const diDiff = cat.di - prev.di;

            return [
                {
                    id: cat.id,
                    name: cat.name,
                    diDiff,
                    deviation: cat.deviation,
                    color: DOT_FILL[i % DOT_FILL.length],
                },
            ];
        });
    }, [scoresByCategory, previousScoresByCategory]);

    const { max: diDiffMax, range: diDiffRange } = useRange(
        points.map((p) => p.diDiff),
        Y_TICK_INCREMENT
    );

    const { max: deviationMax, range: deviationRange } = useRange(
        // This offset is required as `useRange` assumes that the midpoint is at 0,
        // but the deviation of 50 is at the center of the chart.
        points.map((p) => p.deviation - 50),
        X_TICK_INCREMENT
    );

    /** Convert deviation to chart x-coordinate. */
    const toChartX = (deviation: number) => {
        // Revert the offset applied in `useRange`.
        return left + ((deviation - 50 + deviationMax) / deviationRange) * width;
    };

    /** Convert DI difference to chart y-coordinate. */
    const toChartY = (diDiff: number) => {
        return top + ((diDiffMax - diDiff) / diDiffRange) * height;
    };

    const handleDotHover = (dot: Konva.Node, item: TooltipState, active: boolean) => {
        if (active) {
            dot.opacity(1);
            dot.to({ scaleX: 1.6, scaleY: 1.6, duration: 0.15 });
            setTooltip(item);
        } else {
            dot.opacity(DOT_OPACITY);
            dot.to({ scaleX: 1, scaleY: 1, duration: 0.15 });
            setTooltip(null);
        }
    };

    const legendItems = points.map((p) => ({
        ...p,
        x: toChartX(p.deviation),
        y: toChartY(p.diDiff),
    }));

    return (
        <Section>
            <SectionHeader
                title="動​的​分析​"
                description="横軸に今回の偏差値、縦軸に前回からの DI 変化量を取り、カテゴリーを強み・改善・弱み・注意の 4 象限に配置します。"
            />

            <div ref={containerRef} className={styles.container}>
                <Stage ref={stageRef} width={stageWidth} height={STAGE_H}>
                    <Layer>
                        {/* White background for exporting as image */}
                        <Rect x={0} y={0} width={stageWidth} height={STAGE_H} fill="white" />

                        <Quadrants chartDimensions={chartDimensions} />

                        <Axes
                            chartDimensions={chartDimensions}
                            diDiffMax={diDiffMax}
                            diDiffRange={diDiffRange}
                            deviationMax={deviationMax}
                            deviationRange={deviationRange}
                        />

                        {points.map((p) => {
                            const x = toChartX(p.deviation);
                            const y = toChartY(p.diDiff);

                            return (
                                <Circle
                                    key={p.id}
                                    ref={(node) => {
                                        if (node) dotRefs.current.set(p.id, node);
                                        else dotRefs.current.delete(p.id);
                                    }}
                                    x={x}
                                    y={y}
                                    radius={6}
                                    fill={p.color}
                                    opacity={DOT_OPACITY}
                                    onMouseEnter={(e) => {
                                        handleDotHover(e.target, { x, y, ...p }, true);
                                    }}
                                    onMouseLeave={(e) => {
                                        handleDotHover(e.target, { x, y, ...p }, false);
                                    }}
                                />
                            );
                        })}

                        <Legend
                            items={legendItems}
                            onRowHover={(item) => {
                                const dot = dotRefs.current.get(item.id);
                                if (!dot) return;
                                handleDotHover(dot, item, true);
                            }}
                            onRowHoverEnd={(item) => {
                                const dot = dotRefs.current.get(item.id);
                                if (!dot) return;
                                handleDotHover(dot, item, false);
                            }}
                        />

                        {tooltip && <Tooltip state={tooltip} xOrigin={xOrigin} />}
                    </Layer>
                </Stage>
            </div>
        </Section>
    );
}

export function DynamicAnalysisChartSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6">
                <Flex direction="column" gap="1">
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Skeleton.Box height={STAGE_H} />
            </Flex>
        </Section>
    );
}
